import humps from "humps";

type RequestParams = {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  body?: any;
  form?: FormData;
  headers?: HeadersInit;
  params?: Record<string, string | number | boolean>;
  suppressErrorAlert?: boolean;
};

// 从环境变量获取基础 URL
const baseUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
const request = {
  async send({
    method,
    path,
    body,
    form,
    headers = {},
    params,
    suppressErrorAlert = false,
  }: RequestParams): Promise<any> {
    // 构建完整的 URL
    const apiPath = "/api" + path;
    let url = new URL(apiPath, baseUrl).toString();
    if (params) {
      const snakeCaseParams = humps.decamelizeKeys(params);
      const parsedParams = Object.keys(snakeCaseParams)
        .map((key) => `${key}=${snakeCaseParams[key]}`)
        .join("&");
      // const parsedParams = JSON.stringify(snakeCaseParams)
      url = `${url}?${parsedParams}`;
    }
    const snakeCaseBody = body ? humps.decamelizeKeys(body) : null;

    // 设置默认头部
    const defaultHeaders = {};
    const token = localStorage.getItem("token");
    if (token) {
      defaultHeaders["MAuthorization"] = token;
    }

    const init = {
      method,
      headers: { ...defaultHeaders, ...headers },
    };
    if (form) {
      init["body"] = form;
    } else if (snakeCaseBody) {
      init["headers"]["Content-Type"] = "application/json";
      init["body"] = JSON.stringify(snakeCaseBody);
    }

    // 发送请求
    const response: Response = await fetch(url.toString(), init);
    if (!response) {
      return;
    }
    // 检查 Content-Disposition 头
    const contentDisposition = response.headers.get("Content-Disposition");
    const isFileResponse =
      contentDisposition && contentDisposition.includes("attachment");
    if (isFileResponse) {
      // 处理文件响应
      return await response.blob(); // 返回文件数据
    }

    const responseBody = await response.json();
    try {
      // 判断请求是否成功
      if (!responseBody.success) {
        if (response.status === 401) {
          console.log(responseBody.message);
          return responseBody;
        }
        if (!suppressErrorAlert) {
          alert(responseBody.message || "请求失败");
        }
        return responseBody;
      }

      // 如果成功，处理数据并进行驼峰转换
      responseBody.data = humps.camelizeKeys(responseBody.data);
    } catch (error) {
      console.error(error);
    }
    return responseBody;
  },

  // GET 请求
  async get(path: string, params?: Record<string, any>, headers?: HeadersInit) {
    return await this.send({
      method: "GET",
      path: path,
      body: null,
      headers: headers,
      params: params,
    });
  },

  // POST 请求
  async post(
    path: string,
    body: any,
    form?: FormData,
    headers?: HeadersInit,
    options?: { suppressErrorAlert?: boolean },
  ) {
    return await this.send({
      method: "POST",
      path: path,
      body: body,
      form: form,
      headers: headers,
      suppressErrorAlert: options?.suppressErrorAlert,
    });
  },

  // PUT 请求
  async put(path: string, body: any, headers?: HeadersInit) {
    return await this.send({
      method: "PUT",
      path: path,
      body: body,
      headers: headers,
    });
  },

  // DELETE 请求
  async del(
    path: string,
    params?: Record<string, string | number | boolean>,
    headers?: HeadersInit,
  ) {
    return await this.send({
      method: "DELETE",
      path: path,
      headers: headers,
      params: params,
    });
  },
};

export default request;
